import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Search,
    Download,
    LogOut,
    Users,
    Phone,
    School,
    Home as HomeIcon,
    RefreshCcw,
    Loader2,
    Trash2,
    Ticket,
} from "lucide-react";
import { Lotus } from "@/components/decor/Motifs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

export default function AdminDashboard() {
    const nav = useNavigate();
    const token = typeof window !== "undefined" ? localStorage.getItem("iys_admin_token") : null;
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!token) {
            nav("/admin/login");
            return;
        }
        void fetchRows();
    }, []);

    const fetchRows = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/registrations`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRows(data);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                localStorage.removeItem("iys_admin_token");
                nav("/admin/login");
                return;
            }
            toast.error("Failed to load registrations.");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("iys_admin_token");
        localStorage.removeItem("iys_admin_user");
        nav("/admin/login");
    };

    const download = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API}/admin/registrations/export`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `iskcon_festival_registrations_${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV downloaded.");
        } catch {
            toast.error("Export failed.");
        } finally {
            setDownloading(false);
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/admin/registrations/${pendingDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRows((prev) => prev.filter((r) => r.id !== pendingDelete.id));
            toast.success(`Removed ${pendingDelete.student_name}`);
            setPendingDelete(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed.");
        } finally {
            setDeleting(false);
        }
    };

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((r) =>
            [
                r.student_name,
                r.institute,
                r.student_class,
                r.address,
                r.mobile_whatsapp,
                r.mobile_alternate,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(term))
        );
    }, [rows, q]);

    const stats = useMemo(() => {
        const institutes = new Set(rows.map((r) => r.institute.toLowerCase()));
        const withAlt = rows.filter((r) => r.mobile_alternate).length;
        return {
            total: rows.length,
            institutes: institutes.size,
            withAlt,
        };
    }, [rows]);

    return (
        <div className="min-h-screen bg-cream-50 text-inkbrown-900">
            {/* Header */}
            <header className="border-b border-vgold-100 bg-white/70 backdrop-blur sticky top-0 z-20">
                <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="admin-brand-link">
                        <Lotus className="w-7 h-7" />
                        <div className="leading-tight">
                            <p className="font-title text-lg tracking-wide">IYS Admin</p>
                            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-inkbrown-500">
                                Festival 2026 Console
                            </p>
                        </div>
                    </Link>
                    <button
                        onClick={logout}
                        data-testid="admin-logout-btn"
                        className="inline-flex items-center gap-2 rounded-full border-2 border-vgold-400/60 px-4 py-2 font-body text-xs uppercase tracking-[0.25em] text-inkbrown-900 hover:bg-vgold-50 transition-all"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-6 md:px-10 py-10">
                {/* Stats */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatCard
                        icon={Users}
                        label="Total Registrations"
                        value={stats.total}
                        testid="stat-total"
                    />
                    <StatCard
                        icon={School}
                        label="Institutes Represented"
                        value={stats.institutes}
                        testid="stat-institutes"
                    />
                    <StatCard
                        icon={Phone}
                        label="With Alternate Number"
                        value={stats.withAlt}
                        testid="stat-alt"
                    />
                </section>

                {/* Toolbar */}
                <section className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h2 className="font-heading text-3xl md:text-4xl text-inkbrown-900">
                        Registrations
                    </h2>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 rounded-full bg-white border border-vgold-100 px-4 py-2 shadow-sm">
                            <Search className="w-4 h-4 text-inkbrown-500" />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search name, class, mobile…"
                                data-testid="admin-search-input"
                                className="bg-transparent outline-none font-body text-sm w-56"
                            />
                        </div>
                        <button
                            onClick={fetchRows}
                            data-testid="admin-refresh-btn"
                            className="inline-flex items-center gap-2 rounded-full border-2 border-vgold-400/60 px-4 py-2 font-body text-xs uppercase tracking-[0.25em] hover:bg-vgold-50 transition-all"
                        >
                            <RefreshCcw className="w-4 h-4" /> Refresh
                        </button>
                        <button
                            onClick={download}
                            disabled={downloading || rows.length === 0}
                            data-testid="admin-export-csv-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 font-body text-xs uppercase tracking-[0.25em] shadow-glow transition-all"
                        >
                            {downloading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4" />
                            )}
                            Export CSV
                        </button>
                    </div>
                </section>

                {/* Table */}
                <section
                    className="mt-6 rounded-3xl border border-vgold-100 bg-white overflow-hidden shadow-divine"
                    data-testid="registrations-table-container"
                >
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-saffron-50/60">
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    #
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Ticket No.
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Student
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Institute
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Class
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Address
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    WhatsApp
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Alt. Mobile
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500">
                                    Registered
                                </TableHead>
                                <TableHead className="font-body uppercase text-[10px] tracking-[0.25em] text-inkbrown-500 text-right">
                                    Actions
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="text-center py-16">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-saffron-500" />
                                        <p className="mt-3 font-body text-inkbrown-500">
                                            Loading registrations…
                                        </p>
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="text-center py-16">
                                        <HomeIcon className="w-6 h-6 mx-auto text-inkbrown-500" />
                                        <p className="mt-3 font-heading text-lg text-inkbrown-900">
                                            {rows.length === 0
                                                ? "No registrations yet — the courtyard awaits."
                                                : "No matches for your search."}
                                        </p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((r, i) => (
                                    <TableRow
                                        key={r.id}
                                        className="hover:bg-saffron-50/40 transition-colors"
                                        data-testid={`registration-row-${i}`}
                                    >
                                        <TableCell className="font-body text-inkbrown-500">
                                            {i + 1}
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            <span className="inline-flex items-center gap-1.5 rounded-full bg-vgold-50 border border-vgold-200 px-2.5 py-1 text-inkbrown-900 font-semibold">
                                                <Ticket className="w-3 h-3 text-saffron-600" />
                                                {r.ticket_no || "—"}
                                            </span>
                                        </TableCell>
                                        <TableCell className="font-heading text-base text-inkbrown-900">
                                            {r.student_name}
                                        </TableCell>
                                        <TableCell className="font-body">{r.institute}</TableCell>
                                        <TableCell className="font-body">{r.student_class}</TableCell>
                                        <TableCell className="font-body text-sm max-w-[220px] truncate" title={r.address}>
                                            {r.address}
                                        </TableCell>
                                        <TableCell className="font-body text-sm text-saffron-700">
                                            {r.mobile_whatsapp}
                                        </TableCell>
                                        <TableCell className="font-body text-sm">
                                            {r.mobile_alternate || "—"}
                                        </TableCell>
                                        <TableCell className="font-body text-xs text-inkbrown-500">
                                            {formatDate(r.created_at)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <button
                                                onClick={() => setPendingDelete(r)}
                                                data-testid={`delete-registration-btn-${i}`}
                                                aria-label={`Delete ${r.student_name}`}
                                                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-transparent text-inkbrown-500 hover:text-white hover:bg-maroon-600 hover:border-maroon-700 transition-all"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </section>
            </main>

            {/* Delete confirmation dialog */}
            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}
            >
                <AlertDialogContent data-testid="delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-2xl">
                            Delete this registration?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="font-body">
                            You are about to permanently remove{" "}
                            <span className="font-semibold text-inkbrown-900">
                                {pendingDelete?.student_name}
                            </span>{" "}
                            {pendingDelete?.ticket_no ? `(${pendingDelete.ticket_no})` : ""} from
                            the festival roll. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel-btn" disabled={deleting}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-confirm-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                confirmDelete();
                            }}
                            disabled={deleting}
                            className="bg-maroon-600 hover:bg-maroon-700 text-white"
                        >
                            {deleting ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Deleting…
                                </span>
                            ) : (
                                "Yes, delete"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, testid }) {
    return (
        <div
            className="rounded-3xl bg-white border border-vgold-100 p-6 shadow-divine flex items-center gap-4"
            data-testid={testid}
        >
            <div className="w-12 h-12 rounded-2xl bg-saffron-50 border border-saffron-200 flex items-center justify-center">
                <Icon className="w-5 h-5 text-saffron-600" />
            </div>
            <div>
                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-inkbrown-500">
                    {label}
                </p>
                <p className="mt-1 font-title text-3xl text-inkbrown-900">{value}</p>
            </div>
        </div>
    );
}
